import isEqual from "lodash/isEqual";

export interface UnitMatrixFields {
  id?: string;
  __matrixField?: string;
  __matrixFieldType1?: string;
}

interface UnitMatrixReference extends UnitMatrixFields {
  id: string;
  __unitToReplaceId?: string;
}

export interface OriginalUnitMatrixData {
  matrixField: string;
  matrixFieldType1: string;
}

interface BuildOriginalMatrixDataOptions {
  units: UnitMatrixReference[];
  matrixDataByUnitId: Record<string, string>;
  inertiaDataByUnitId: Record<string, string>;
  isEdit?: boolean;
}

export const buildOriginalMatrixData = ({
  units,
  matrixDataByUnitId,
  inertiaDataByUnitId,
  isEdit,
}: BuildOriginalMatrixDataOptions): Record<string, OriginalUnitMatrixData> =>
  units.reduce<Record<string, OriginalUnitMatrixData>>((originalData, unit) => {
    const sourceUnitId = isEdit ? unit.id : unit.__unitToReplaceId;
    if (sourceUnitId) {
      originalData[unit.id] = {
        matrixField: matrixDataByUnitId[sourceUnitId] ?? "",
        matrixFieldType1: inertiaDataByUnitId[sourceUnitId] ?? "",
      };
    }
    return originalData;
  }, {});

const normalizeMatrixValue = (value: unknown): unknown => {
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  if (Array.isArray(value)) {
    return value.map(normalizeMatrixValue);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, normalizeMatrixValue(entry)]));
  }
  return value;
};

const parseMatrixField = (value?: string): unknown => {
  if (!value) return undefined;

  try {
    return normalizeMatrixValue(JSON.parse(value));
  } catch {
    return value;
  }
};

export const hasMatrixParametersChanged = (
  units: UnitMatrixFields[],
  originalMatrixData: Record<string, OriginalUnitMatrixData>
): boolean =>
  units.some((unit) => {
    if (!unit.id) return false;

    const original = originalMatrixData[unit.id];
    if (!original) return false;

    return (
      !isEqual(parseMatrixField(unit.__matrixField), parseMatrixField(original.matrixField)) ||
      !isEqual(parseMatrixField(unit.__matrixFieldType1), parseMatrixField(original.matrixFieldType1))
    );
  });
